import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  loginService,
  verifyOtpService,
  registerService,
  getProfileService,
  updateProfileService,
  socialLoginService,
  logoutService,
  changePasswordService,
  forgotPasswordService,
  forgotPasswordOtpService,
} from './authService';
import { setToken, getToken, getRefreshToken, getMemberId, removeToken, setUserInfo, getUserInfo } from '../../utils/tokenHelper';

// Login Action
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await loginService(credentials);

      // Store email temporarily for OTP verification
      if (credentials?.email) {
        sessionStorage.setItem('pendingEmail', credentials.email);
      }

      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Incorrect email or password. Please try again.');
    }
  }
);

// Verify OTP Action
export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (otpData, { rejectWithValue }) => {
    try {
      const response = await verifyOtpService(otpData);

      // response now has { status, data, message, ...rawResponse }
      const responseData = response?.data || {};

      // Normalize isRegister field
      const isRegister = responseData?.isRegister ?? responseData?.isRegistered ?? false;

      // Get authToken (accessToken) from response
      const accessToken = responseData?.authToken || responseData?.accessToken;
      const refreshToken = responseData?.refreshToken;
      const memberId = responseData?.memberId;

      // Build user object from response data
      const user = {
        email: responseData?.email || otpData.email,
        name: responseData?.name || (responseData?.email ? responseData.email.split('@')[0] : ''),
        memberId: memberId,
        isRegistered: isRegister,
      };

      // If tokens are returned and user is registered, store them in localStorage
      if (accessToken && (isRegister || responseData?.isRegisterRequired === true)) {
        setToken(accessToken, refreshToken, memberId);

        // Store user info in localStorage for persistence
        setUserInfo(user);
      }

      // Return normalized response with isRegister field AND user object
      return {
        ...responseData,
        ...response,
        isRegister,
        isRegistered: isRegister,
        accessToken,
        refreshToken,
        memberId,
        user: isRegister ? user : null, // Include user object when registered
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'OTP verification failed. Please try again.');
    }
  }
);

// Register/Signup Action
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await registerService(userData);

      // response now has { status, data, message, ...rawResponse }
      const responseData = response?.data || {};
      const accessToken = responseData?.authToken || responseData?.accessToken;
      const refreshToken = responseData?.refreshToken;
      const memberId = responseData?.memberId;

      // Store tokens after successful registration in localStorage
      if (accessToken) {
        setToken(accessToken, refreshToken, memberId);

        // Store user info in localStorage for persistence
        const userInfo = {
          email: userData.email,
          name: userData.name,
          memberId: memberId,
          businessName: userData.businessName,
          mobileNumber: userData.mobileNumber,
          address: userData.address,
          isRegistered: true,
        };
        setUserInfo(userInfo);

        // Update Redux state with user data
        return {
          ...response,
          Data: responseData,
          user: userInfo,
          isAuthenticated: true,
          accessToken,
          refreshToken,
          memberId,
        };
      }

      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Registration failed. Please try again.');
    }
  }
);

// Helper to normalize profile data from any source (API response or submitted form)
const normalizeProfile = (data) => {
  const defaultProfile = {
    memberId: null,
    name: '',
    email: '',
    businessName: '',
    businessCategory: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstIn: '',
    website: '',
    logo: '',
    role: 'user',
    status: 'active',
  };

  return {
    ...defaultProfile,
    ...data,
    memberId: data.memberId || data.MemberId || data.id || defaultProfile.memberId,
    name: data.name || data.Name || data.fullName || defaultProfile.name,
    email: data.email || data.Email || defaultProfile.email,
    businessName: data.businessName || data.BusinessName || data.company || defaultProfile.businessName,
    businessCategory: data.businessCategory || data.BusinessCategory || defaultProfile.businessCategory,
    mobileNumber: data.mobileNumber || data.MobileNumber || data.mobile || data.Mobile || defaultProfile.mobileNumber,
    address: data.address || data.Address || defaultProfile.address,
    city: data.city || data.City || defaultProfile.city,
    state: data.state || data.State || defaultProfile.state,
    pincode: data.pincode || data.Pincode || defaultProfile.pincode,
    gstIn: data.gstIn || data.GSTIn || data.gstin || defaultProfile.gstIn,
    website: data.website || data.Website || defaultProfile.website,
    logo: data.logo || data.Logo || defaultProfile.logo,
  };
};

// Get User Profile
export const getUserProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProfileService();
      // getProfileService already returns normalized data, but we re-normalize for safety
      return normalizeProfile(response?.data || response || {});
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch profile.');
    }
  }
);

// Update User Profile - with merge of submitted and returned data
export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await updateProfileService(profileData);

      // Extract data from the response wrapper (handleResponse puts it in .data)
      const returnedData = response?.data || {};

      // Convert FormData to plain object if needed so we can spread it
      let submittedData = {};
      if (profileData instanceof FormData) {
        submittedData = Object.fromEntries(profileData.entries());
      } else {
        submittedData = profileData;
      }

      // Merge and normalize the combined data
      const finalProfile = normalizeProfile({
        ...submittedData,
        ...returnedData,
      });

      return finalProfile;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to update profile.');
    }
  }
);

// Social Login
export const socialLoginUser = createAsyncThunk(
  'auth/socialLogin',
  async (socialData, { rejectWithValue }) => {
    try {
      const response = await socialLoginService(socialData);
      const responseData = response?.data || {};

      const accessToken = responseData?.authToken || responseData?.accessToken || responseData?.AuthToken;
      const refreshToken = responseData?.refreshToken || responseData?.RefreshToken;
      const memberId = responseData?.memberId || responseData?.MemberId;

      const user = {
        email: responseData?.email || responseData?.Email,
        name: responseData?.name || responseData?.Name || responseData?.firstName || responseData?.FirstName,
        memberId: memberId,
        isRegistered: !(responseData?.isRegisterRequired ?? responseData?.IsRegisterRequired),
      };

      if (accessToken) {
        setToken(accessToken, refreshToken, memberId);
        
        // Persist user info in localStorage
        setUserInfo(user);
      }

      return {
        ...response,
        accessToken,
        refreshToken,
        memberId,
        user,
        isRegisterRequired: responseData?.isRegisterRequired ?? responseData?.IsRegisterRequired,
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Social login failed. Please try again.');
    }
  }
);

// Change Password
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await changePasswordService(passwordData);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to change password.');
    }
  }
);

// Request Forgot Password OTP
export const requestForgotPasswordOtp = createAsyncThunk(
  'auth/requestForgotPasswordOtp',
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordOtpService(email);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to send OTP.');
    }
  }
);

// Forgot Password
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (forgotPasswordData, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordService(forgotPasswordData);
      return response;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to process forgot password request.');
    }
  }
);

// Logout
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await logoutService();
    } catch (error) {
      console.log('[Auth] Logout API error, clearing local tokens anyway');
    } finally {
      removeToken();
    }
    return { success: true };
  }
);

// Initialize Auth - Check for existing session on app load
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Check localStorage for existing tokens using helper functions
      const token = getToken();
      const refreshToken = getRefreshToken();
      const memberId = getMemberId();

      console.log('[Auth] Initializing with tokens:', { hasToken: !!token, hasRefreshToken: !!refreshToken, memberId });

      // If no tokens found, user is not authenticated
      if (!token || !memberId) {
        removeToken(); // Clean up any lingering data (like userInfo/businessType) from previous sessions by default
        return { isAuthenticated: false };
      }

      // Try to get user profile to validate token and get user data
      // But DON'T remove tokens if profile fails - API might be down
      let user = null;
      try {
        user = await dispatch(getUserProfile()).unwrap();
      } catch (profileError) {
        // Profile fetch failed - try to refresh token
        console.log('[Auth] Profile fetch failed:', profileError?.message);

        // Try refresh token to see if that's the issue
        try {
          // Import refresh logic or handle here if needed
          // For now, keep existing tokens and try to get user info from storage
        } catch (refreshError) {
          console.log('[Auth] Token refresh also failed:', refreshError?.message);
        }

        // Try to get user info from localStorage as fallback
        const storedUserInfo = getUserInfo();
        user = storedUserInfo || {
          email: sessionStorage.getItem('pendingEmail') || '',
          memberId: memberId,
          name: '',
          // Static defaults for missing profile data
          firstName: '',
          lastName: '',
          mobileNumber: '',
          businessName: '',
          address: '',
          city: '',
          state: '',
          country: '',
          logo: '',
        };
      }

      // Mark as authenticated if we have tokens (even if profile failed)
      return {
        isAuthenticated: true,
        user,
        token,
        refreshToken,
        memberId,
      };
    } catch (error) {
      console.error('[Auth] Initialization error:', error);
      return rejectWithValue('Session expired. Please login again.');
    }
  }
);
